import { UseQueryResult } from '@tanstack/react-query';
import {
  GridColDef,
  GridSlotsComponent,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router-dom';
import { GPanelContactsPagesConfig } from '../pages-config.ts';
import { Dispatch, ReactNode, SetStateAction } from 'react';
import SharedGpanelGrid from './shared-gpanel-grid';
import { useLabelsQuery } from '../../gpanel-contacts-queries.ts';
import SharedDataGridClient, {
  DefaultGridState,
} from '../../../../../shared/shared-data-grid-client.tsx';
import { GridStateKey } from '../../../../../../types.ts';

export default function SharedGpanelContactsGrid<T extends GridValidRowModel>({
  query,
  onRefresh,
  onAddToLabel,
  onSelection,
  selection,
  title,
  columns,
  toolbar,
  noRowsOverlay,
  noResultsOverlay,
  headerActions,
  contactsToolbarActions,
  disableRowClick = false,
  initialGridState,
  gridStateKey,
  gridKey,
}: {
  query: UseQueryResult<T[], Error>;
  onRefresh?: () => void;
  onAddToLabel?: () => void;
  disableRowClick?: boolean;
  onSelection: Dispatch<SetStateAction<number[]>>;
  selection: number[];
  title: string;
  columns: GridColDef<T>[];
  toolbar?: GridSlotsComponent['toolbar'];
  noRowsOverlay: () => React.JSX.Element;
  noResultsOverlay?: () => React.JSX.Element;
  headerActions?: React.ReactNode;
  contactsToolbarActions?: (selection: number[]) => ReactNode;
  initialGridState?: DefaultGridState | undefined;
  gridStateKey: GridStateKey;
  gridKey?: string | null;
}) {
  const navigate = useNavigate();

  const { data: labels = [] } = useLabelsQuery();
  const contactWithActiveLabel = query.data?.map((contact) => ({
    ...contact,
    activeLabels: labels
      .filter((label) => contact.labelIds?.includes(label.id) && !label.trashed)
      .map((label) => label.name),
  }));

  return (
    <SharedGpanelGrid>
      <SharedDataGridClient
        gridKey={gridKey}
        gridStateKey={gridStateKey}
        columns={columns}
        rows={contactWithActiveLabel || []}
        loading={query.isFetching}
        getRowId={(row) => row.id}
        checkboxSelection
        disableRowSelectionOnClick
        defaultGridState={initialGridState}
        selectedRows={selection}
        setSelectedRows={onSelection}
        sx={{
          '& .MuiDataGrid-cell': {
            alignContent: 'center',
            alignItems: 'center',
          },
          '& .MuiDataGrid-row': {},
          '&.MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: (theme) => theme.palette.grey[200],
          },
          ...(!disableRowClick
            ? {
                '& .MuiDataGrid-row:hover': {
                  cursor: 'pointer',
                },
              }
            : {
                '& .MuiDataGrid-row:hover': {
                  cursor: 'default',
                },
              }),
        }}
        onRowClick={(params) => {
          if (!disableRowClick) {
            navigate(
              `./${GPanelContactsPagesConfig.ContactsDetails.link}/${params.row.id}`
            );
          }
        }}
        slots={{ toolbar, noRowsOverlay, noResultsOverlay }}
        slotProps={{
          toolbar: {
            isLoading: query.isFetching,
            selection,
            onRefresh,
            onAddToLabel,
            contactsToolbarActions,
            title,
            actions: headerActions,
          } as never,
        }}
      />
    </SharedGpanelGrid>
  );
}
